import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-counsel-1/artifacts/4d69y67q_IMG_0270.jpeg";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/#about" },
  { label: "Services", href: "/#services" },
  { label: "Diaspora", href: "/diaspora" },
  { label: "Insights", href: "/insights/diaspora-property-guide" },
  { label: "Packages", href: "/packages" },
  { label: "Consultation", href: "/#booking" },
  { label: "Contact", href: "/#booking" },
];

export const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (e, href) => {
    if (href.includes("#")) {
      if (href.startsWith("/#") && location.pathname === "/") {
        e.preventDefault();
        const element = document.querySelector(href.replace("/", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#E5E5E5] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 md:gap-3"
            data-testid="nav-logo"
          >
            <img
              src={LOGO_URL}
              alt="4 ACES Attorneys"
              className="h-9 md:h-12 w-auto"
            />
            <div className="hidden xs:block">
              <span className={`font-['Playfair_Display'] text-sm md:text-base lg:text-lg font-bold tracking-wide ${scrolled ? "text-[#1A1A1A]" : "text-white"}`}>
                4 ACES ATTORNEYS
              </span>
              <span className={`block text-[8px] md:text-[9px] lg:text-[10px] tracking-[0.1em] md:tracking-[0.12em] ${scrolled ? "text-[#1A1A1A]/50" : "text-white/50"}`}>
                Private Property & Real Estate Law
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              link.href.startsWith("/") && !link.href.includes("#") ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-xs xl:text-sm font-medium tracking-wide uppercase transition-colors ${
                    scrolled 
                      ? "text-[#1A1A1A]/70 hover:text-[#D4AF37]" 
                      : "text-white/80 hover:text-[#D4AF37]"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-xs xl:text-sm font-medium tracking-wide uppercase transition-colors ${
                    scrolled 
                      ? "text-[#1A1A1A]/70 hover:text-[#D4AF37]" 
                      : "text-white/80 hover:text-[#D4AF37]"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-[#1A1A1A]" : "text-white"}`}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden bg-white border-t border-[#E5E5E5] shadow-lg"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-6 space-y-1">
            {navLinks.map((link) => (
              link.href.startsWith("/") && !link.href.includes("#") ? (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-[#1A1A1A] py-3 text-base font-medium tracking-wide transition-colors hover:text-[#D4AF37] border-b border-[#E5E5E5]/50"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-[#1A1A1A] py-3 text-base font-medium tracking-wide transition-colors hover:text-[#D4AF37] border-b border-[#E5E5E5]/50"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
